import { useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import { Box, useThemeUI } from "theme-ui";
import { useDispatch } from "react-redux";
import queryString from "query-string";
import flow from "lodash/fp/flow";
import identity from "lodash/fp/identity";

import createI18n from "config/i18n";
import { useAddCss } from "hooks";
import { queryStringParse } from "hooks/useSetQueryString";
import {
  fetchProductFormData,
  fetchRecipe,
  setFormData,
} from "store/app/appSlice";
import { LoadingIndicator } from "components";
import { nestedObjectReplaceValues, translateNestedObject } from "utils";
import {
  updateFormDataFromRecipeData,
  updateFormDataFromVolumentalData,
} from "store/app";

export default function LoadData({
  productFormDataUrl,
  publicDirectoryUrl,
  locale,
  children,
}) {
  const queryParams = queryStringParse(window.location?.search || "");
  const { family, recipeID, ...initialUserChoiceValues } = queryParams;

  let volumentalData = JSON.parse(queryParams?.volumental_data || "{}");

  const {
    theme: { isMobile },
  } = useThemeUI();

  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [i18n, setI18nInstance] = useState(null);

  useAddCss({
    href: publicDirectoryUrl + "/fonts/fonts.css",
  });

  useEffect(() => {
    const fetchProductFormDataPromise = dispatch(
      fetchProductFormData({
        productFormDataUrl,
      })
    );

    const imagesQueryString = queryString.stringify({
      fmt: "auto",
      qlt: 80,
      maxW: isMobile ? 1000 : 2000,
      maxH: isMobile ? 1000 : 2000,
    });

    const fetchRecipePromise = recipeID && dispatch(fetchRecipe({ recipeID }));

    const promises = [fetchProductFormDataPromise, fetchRecipePromise].filter(
      Boolean
    );

    Promise.all(promises.map((promise) => promise.unwrap()))
      .then(([formData, recipeData]) => {
        return {
          formData: flow(
            updateFormDataFromVolumentalData(volumentalData),
            recipeData ? updateFormDataFromRecipeData(recipeData) : identity,
            nestedObjectReplaceValues(".webp", `?${imagesQueryString}`)
          )(formData),
          recipeData,
        };
      })
      .then(({ formData, recipeData }) =>
        createI18n({
          publicDirectoryUrl,
          locale,
          product: formData.product,
        }).then((i18nInstance) => ({ formData, recipeData, i18nInstance }))
      )
      .then(({ formData, recipeData, i18nInstance }) => {
        flow(
          translateNestedObject(i18nInstance),
          (processedFormData) =>
            setFormData({
              formData: processedFormData,
              family,
              recipeData,
              volumentalData,
              initialUserChoiceValues,
            }),
          dispatch
        )(formData);

        setI18nInstance(i18nInstance);
        setIsLoading(false);
      })
      .catch((e) => {
        console.error(e);
      });

    return () => {
      promises.forEach((promise) => promise.abort("cancelled"));
    };
  }, [productFormDataUrl, recipeID]);

  if (isLoading || !i18n) {
    return (
      <Box
        sx={{
          overflow: "hidden",
          backgroundColor: "background",
          color: "text",
          fontFamily: "body",
          height: "100%",
          position: "relative",
          width: "100%",
        }}
      >
        <LoadingIndicator
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
      </Box>
    );
  }

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
